/**
 * Redondeo, formato y lectura de números (design.md, sección 4.2).
 *
 * Regla del proyecto: se guarda el número completo y se redondea UNA sola vez,
 * al presentar. Estas funciones son ese único punto de redondeo.
 */

/**
 * Redondea a `decimales` con la regla "la mitad se aleja de cero".
 *
 * Usa la representación decimal del número (`"1.005e2"`) en lugar de
 * multiplicar, porque 1.005 × 100 da 100.49999999999999 en punto flotante y
 * redondearía mal. Nunca devuelve −0.
 */
export function redondear(valor: number, decimales = 0): number {
  if (!Number.isFinite(valor)) return valor;
  if (!Number.isInteger(decimales) || decimales < 0 || decimales > 10) {
    throw new RangeError(`Los decimales deben ser un entero de 0 a 10, no ${decimales}.`);
  }

  const absoluto = Math.abs(valor);
  const texto = String(absoluto);
  const redondeado = texto.includes('e')
    ? Math.round(absoluto * 10 ** decimales) / 10 ** decimales
    : Number(`${Math.round(Number(`${texto}e${decimales}`))}e-${decimales}`);

  const resultado = valor < 0 ? -redondeado : redondeado;
  return resultado === 0 ? 0 : resultado;
}

/** Redondea al múltiplo más cercano, por ejemplo a 10 kcal (Req. 22.8). */
export function redondearAMultiplo(valor: number, multiplo: number): number {
  if (!(multiplo > 0)) throw new RangeError('El múltiplo debe ser mayor que cero.');
  const resultado = redondear(valor / multiplo, 0) * multiplo;
  return resultado === 0 ? 0 : resultado;
}

export interface OpcionesFormato {
  /** Decimales a los que se redondea. */
  decimales: number;
  /**
   * true = siempre muestra esos decimales ("70.0").
   * false = quita los ceros sobrantes ("22.5", "40").
   */
  fijos?: boolean;
}

/** Signo menos tipográfico para presentar negativos (−400). */
const MENOS = '\u2212';

/**
 * Presenta un número con punto decimal y comas de miles, como se usa en
 * México: 1,505 · 70.0 · −400. No usa Intl, para que el texto sea igual en
 * todos los dispositivos.
 */
export function formatearNumero(valor: number, opciones: OpcionesFormato): string {
  const { decimales, fijos = true } = opciones;
  if (!Number.isFinite(valor)) return '—';

  const redondeado = redondear(valor, decimales);
  const absoluto = Math.abs(redondeado);
  const texto = fijos ? absoluto.toFixed(decimales) : String(absoluto);

  const [entero = '0', fraccion] = texto.split('.');
  const conMiles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const cuerpo = fraccion === undefined || fraccion === '' ? conMiles : `${conMiles}.${fraccion}`;

  return redondeado < 0 ? `${MENOS}${cuerpo}` : cuerpo;
}

/**
 * Lee un número escrito por el usuario. Acepta punto o coma como separador
 * decimal (Req. 5.9) y espacios alrededor. Devuelve null si no es un número
 * válido: vacío, letras, dos separadores o notación científica.
 *
 * Los campos de captura no llevan separador de miles, así que una coma
 * siempre es el separador decimal: "70,5" es 70.5.
 */
export function leerDecimal(texto: string): number | null {
  const limpio = texto.trim().replace(',', '.');
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(limpio)) return null;
  const numero = Number(limpio);
  return Number.isFinite(numero) ? numero : null;
}
